import { Controller } from "@hotwired/stimulus"

// Extracts dominant colors from a persona's background image
// and applies them as CSS custom properties for chat theming.
export default class extends Controller {
  static values = {
    persona: String,
    personaId: Number,
    image: String
  }

  connect() {
    this.applyTheme()
  }

  personaValueChanged() {
    this.applyTheme()
  }

  async applyTheme() {
    const slug = this.personaValue
    if (!slug) { this.clearTheme(); return }

    // Try to get background URL from avatar controller's cached asset data, or persona-state
    let imagePath = this.imageValue
    if (!imagePath) {
      const avatarEl = document.querySelector('[data-controller="avatar"]')
      const assetData = avatarEl?.avatarController?._assetData
      if (assetData?.background_url) {
        imagePath = assetData.background_url
      }
    }
    if (!imagePath) {
      const stateEl = document.getElementById('persona-state')
      if (stateEl?.dataset.bgUrl) imagePath = stateEl.dataset.bgUrl
    }
    if (!imagePath) { this.clearTheme(); return }

    // Check localStorage cache first
    const cacheKey = `theme_${slug}`
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
      try {
        const colors = JSON.parse(cached)
        this.applyColors(colors, imagePath)
        return
      } catch { /* re-extract */ }
    }

    // Try loading the background image
    try {
      const colors = await this.extractColors(imagePath)
      localStorage.setItem(cacheKey, JSON.stringify(colors))
      this.applyColors(colors, imagePath)
    } catch {
      // No background image — use defaults
      this.clearTheme()
    }
  }

  extractColors(imagePath) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = "anonymous"
      img.onload = () => {
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")
        // Sample at small size for performance
        const size = 64
        canvas.width = size
        canvas.height = size
        ctx.drawImage(img, 0, 0, size, size)
        const data = ctx.getImageData(0, 0, size, size).data

        const colors = this.analyzeColors(data)
        resolve(colors)
      }
      img.onerror = reject
      img.src = imagePath
    })
  }

  analyzeColors(data) {
    // Bucket pixels into light/dark/saturated groups
    let rSum = 0, gSum = 0, bSum = 0
    let darkR = 0, darkG = 0, darkB = 0, darkCount = 0
    let satR = 0, satG = 0, satB = 0, satCount = 0
    const total = data.length / 4

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2]
      rSum += r; gSum += g; bSum += b

      const lum = (r * 0.299 + g * 0.587 + b * 0.114) / 255
      const max = Math.max(r, g, b), min = Math.min(r, g, b)
      const sat = max === 0 ? 0 : (max - min) / max

      if (lum < 0.3) {
        darkR += r; darkG += g; darkB += b; darkCount++
      }
      if (sat > 0.3 && lum > 0.15 && lum < 0.85) {
        satR += r; satG += g; satB += b; satCount++
      }
    }

    // Average color
    const avgR = Math.round(rSum / total)
    const avgG = Math.round(gSum / total)
    const avgB = Math.round(bSum / total)

    // Derive background: lighten average significantly
    const bgR = Math.min(255, avgR + Math.round((255 - avgR) * 0.85))
    const bgG = Math.min(255, avgG + Math.round((255 - avgG) * 0.85))
    const bgB = Math.min(255, avgB + Math.round((255 - avgB) * 0.85))

    const bgAltR = Math.min(255, avgR + Math.round((255 - avgR) * 0.78))
    const bgAltG = Math.min(255, avgG + Math.round((255 - avgG) * 0.78))
    const bgAltB = Math.min(255, avgB + Math.round((255 - avgB) * 0.78))

    // Accent: most saturated color, or fall back to default
    let accent, accentDk
    if (satCount > total * 0.02) {
      const sR = Math.round(satR / satCount)
      const sG = Math.round(satG / satCount)
      const sB = Math.round(satB / satCount)
      accent = this.rgb(sR, sG, sB)
      accentDk = this.rgb(
        Math.round(sR * 0.8),
        Math.round(sG * 0.8),
        Math.round(sB * 0.8)
      )
    } else {
      accent = null
      accentDk = null
    }

    // Border: between bg and average
    const borderR = Math.min(255, avgR + Math.round((255 - avgR) * 0.55))
    const borderG = Math.min(255, avgG + Math.round((255 - avgG) * 0.55))
    const borderB = Math.min(255, avgB + Math.round((255 - avgB) * 0.55))

    return {
      bg: this.rgb(bgR, bgG, bgB),
      bgAlt: this.rgb(bgAltR, bgAltG, bgAltB),
      accent,
      accentDk,
      border: this.rgb(borderR, borderG, borderB),
      borderLt: this.rgb(
        Math.min(255, borderR + 20),
        Math.min(255, borderG + 20),
        Math.min(255, borderB + 20)
      )
    }
  }

  rgb(r, g, b) {
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }

  applyColors(colors, imagePath) {
    const el = this.element

    // Apply extracted colors to CSS custom properties
    el.style.setProperty('--color-bg', colors.bg)
    el.style.setProperty('--color-bg-alt', colors.bgAlt)
    el.style.setProperty('--color-border', colors.border)
    el.style.setProperty('--color-border-lt', colors.borderLt)

    if (colors.accent) {
      el.style.setProperty('--color-accent', colors.accent)
      el.style.setProperty('--color-accent-dk', colors.accentDk)
    }

    // Apply background image to the avatar panel (behind the 3D model)
    const panel = document.getElementById('avatar-panel')
    if (panel) {
      panel.style.backgroundImage = `url(${imagePath})`
      panel.style.backgroundSize = 'cover'
      panel.style.backgroundPosition = 'center'
      panel.style.backgroundColor = 'transparent'
    }
  }

  clearTheme() {
    const el = this.element
    const props = ['--color-bg', '--color-bg-alt', '--color-border', '--color-border-lt', '--color-accent', '--color-accent-dk']
    props.forEach(p => el.style.removeProperty(p))

    const panel = document.getElementById('avatar-panel')
    if (panel) {
      panel.style.backgroundImage = ''
      panel.style.backgroundSize = ''
      panel.style.backgroundPosition = ''
      panel.style.backgroundColor = ''
    }
  }

  disconnect() {
    this.clearTheme()
  }
}
